import TR from 'utils/TR';
import T from 'utils/T';
import ex from 'utils/ex';
import VBox from 'components/common/VBox';
import HBox from 'components/common/HBox';
import FocusableButton from 'components/common/FocusableButton';
import Input from 'components/common/Input';
import SelectButton from 'components/common/SelectButton';
import React from 'react';
import Recoil from 'recoil';
import * as AppStore from 'stores/AppStore';
import useForm from 'hooks/common/useForm';
import { luxonClone } from 'utils/time';
import * as Luxon from 'luxon';
import Circle from 'components/common/Circle';
import ExerciseDialogChart from 'components/charts/ExerciseDialogChart';
import CpfRatioBox from 'components/pages/Report/ReportDiet/CpfRatioBox';
import ExerciseDialogExcerciseTable from './ExerciseDialogExcerciseTable';
import ExerciseDialogDietTable from './ExerciseDialogDietTable';
import ExerciseDialogBloodSugarTable from './ExerciseDialogBloodSugarTable';
import ExerciseDialogInsulinTable from './ExerciseDialogInsulinTable';
import usePopupActive from 'hooks/communication/usePopupActive';
import ExerciseDialogGlocoseChart from 'components/charts/ExerciseDialogGlocoseChart';
import ExerciseDialogStepsChart from 'components/charts/ExerciseDialogStepsChart';
import WEEK_DAY_COLUMNS from 'constants/WEEK_DAY_COLUMNS';

export default function ExerciseDialog(props) {
  const {
    index,
    count,
    onPrev,
    onNext,
    events,
    luxon = Luxon.DateTime.now(),
    ...rest
  } = props;

  const {
    requestPopupActive,
  } = usePopupActive();

  const [data, setData] = React.useState({});

  const {
    bldsugr_graph_list = [],
    // bld_sugr_time,
    // bld_sugr_value,
    step_cnt_graph_list = [],
    // active_time,
    // step_cnt,
    daily_step_cnt,
    daily_step_time,
    postprand2h_step_cnt,
    postprand2h_step_time,
    daily_burn_cal,
    postprand2h_burn_cal,
    max_bldsugr_val,
    max_bldsugr_time,
    max_bldsugr_tmzone_cd,
    min_bldsugr_val,
    min_bldsugr_time,
    min_bldsugr_tmzone_cd,
    max_bldsugr_rise_w,
    avg_bldsugr,
    postprand2h_bldsugr_val,
    total_abnormal_cnt,
    total_high_cnt,
    total_low_cnt,
    avg_intaken_cal,
    total_intaken_cal,
    carbo_rate,
    protein_rate,
    fat_rate,
    total_insulin_amt,
    total_bolus_amt,
    total_giju_amt,
    bolus_inject_list = [],
    // bolus_inject_time,
    // bolus_inject_amt,
  } = data;

  const patient_no = Recoil.useRecoilValue(AppStore.assets.patient_no);
  React.useEffect(() => {
    requestPopupActive({
      patient_no,
      search_time: luxon.toFormat('yyyyMMddHHmmss'),
    }, (success, data) => {
      if (success) {
        setData(() => data);
      } else {
        setData(() => ({}));
      }
    })
  }, [luxon, patient_no, requestPopupActive]);

  const [date, weekday] = React.useMemo(() => [luxon.toFormat('yy.MM.dd'), luxonClone(luxon).weekday], [luxon]);

  return (
    <VBox
      {...ex(['Dialog', 'ExerciseDialog'], rest)}
    >
      <HBox {...ex('DialogTitle')}>
        <div>
          {T('{0} ({1})', date, WEEK_DAY_COLUMNS[weekday - 1])}
        </div>
        <div>
          <FocusableButton
            {...{
              onClick: events?.onClose,
            }}
          >
            <svg width={24} height={24} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" clipRule="evenodd" d="M6.29279 6.29308C6.48031 6.10561 6.73462 6.00029 6.99979 6.00029C7.26495 6.00029 7.51926 6.10561 7.70679 6.29308L11.9998 10.5861L16.2928 6.29308C16.385 6.19757 16.4954 6.12139 16.6174 6.06898C16.7394 6.01657 16.8706 5.98898 17.0034 5.98783C17.1362 5.98668 17.2678 6.01198 17.3907 6.06226C17.5136 6.11254 17.6253 6.18679 17.7192 6.28069C17.8131 6.37458 17.8873 6.48623 17.9376 6.60913C17.9879 6.73202 18.0132 6.8637 18.012 6.99648C18.0109 7.12926 17.9833 7.26048 17.9309 7.38249C17.8785 7.50449 17.8023 7.61483 17.7068 7.70708L13.4138 12.0001L17.7068 16.2931C17.8889 16.4817 17.9897 16.7343 17.9875 16.9965C17.9852 17.2587 17.88 17.5095 17.6946 17.6949C17.5092 17.8803 17.2584 17.9855 16.9962 17.9878C16.734 17.99 16.4814 17.8892 16.2928 17.7071L11.9998 13.4141L7.70679 17.7071C7.51818 17.8892 7.26558 17.99 7.00339 17.9878C6.74119 17.9855 6.49038 17.8803 6.30497 17.6949C6.11956 17.5095 6.01439 17.2587 6.01211 16.9965C6.00983 16.7343 6.11063 16.4817 6.29279 16.2931L10.5858 12.0001L6.29279 7.70708C6.10532 7.51955 6 7.26525 6 7.00008C6 6.73492 6.10532 6.48061 6.29279 6.29308Z" fill="#5B6871" />
            </svg>
          </FocusableButton>
        </div>
      </HBox>
      <HBox full>
        <VBox full>
          <VBox full end>
            <ExerciseDialogGlocoseChart {...{ bldsugr_graph_list }} />
            <ExerciseDialogStepsChart {...{ step_cnt_graph_list }} />
          </VBox>
          <VBox>
            <HBox {...ex('Caption')}>
              {T('활동')}
            </HBox>
            <ExerciseDialogExcerciseTable {...{ data }} />
          </VBox>
        </VBox>

        <VBox full >
          <VBox>
            <HBox {...ex('Caption')}>
              {T('혈당')}
            </HBox>
            <ExerciseDialogBloodSugarTable  {...{ data }} />
          </VBox>
          <VBox>
            <HBox {...ex('Caption')}>
              {T('식사')}
            </HBox>
            <ExerciseDialogDietTable {...{ data }} />
          </VBox>
          <VBox>
            <HBox {...ex('Caption')}>
              {T('인슐린')}
            </HBox>
            <ExerciseDialogInsulinTable  {...{ data }} />
          </VBox>
        </VBox>
      </HBox>
    </VBox >
  );
}
